"use client"

import { useRef, useState, useCallback, useEffect } from "react"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { getNonUmbracoUrl } from "utils"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay } from "swiper/modules"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"

import GradientChevron from "../../icons/GradientChevron"
import ArrowDown from "../../icons/ArrowDown"
import PauseIcon from "../../icons/PauseIcon"
import PlayIcon from "../../icons/PlayIcon"
import HeroIntro from "./HeroIntro"
import {
  Root,
  SwiperWrapper,
  SlideImageWrapper,
  DesktopImage,
  MobileImage,
  SlideVideo,
  Overlay,
  ContentLayer,
  MainContent,
  DecorativeImageWrapper,
  TextContent,
  TitleBlock,
  TitleTextWrapper,
  TitleText,
  SubtitleRow,
  SubtitleTextWrapper,
  SubtitleText,
  ChevronAccent,
  CtaLink,
  CtaAnchor,
  CtaSlot,
  BottomStripOuter,
  BottomStrip,
  ScrollIndicator,
  CircleIconButton,
  ScrollText,
  CarouselNavContainer,
  CarouselNavBars,
  NavBarButton,
  NavBar,
  NavBarProgress,
  ContentFadeIn,
} from "./styles"
import type { CtaState } from "./styles"

// EGDE-331: brand lockup shown during the intro reveal — not CMS content, same
// status as the header logo.
// EGDE-634: the wordmark and tagline were hardcoded Hebrew literals, so the /en
// splash opened the English site with a Hebrew lockup. They live in the message
// files now (`Homepage.brandWordmark` / `Homepage.brandTagline`) — the Hebrew
// values are unchanged.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type HeroBannerProps = components["schemas"]["HeroBannerPropertiesModel"]
type HeroBannerSlideProps =
  components["schemas"]["HeroBannerSlidePropertiesModel"]
type ApiLink = components["schemas"]["ApiLinkModel"]

interface HeroCta {
  label: string
  href: string
  isInternal: boolean
  target?: string
}

interface Props {
  data: HeroBannerProps
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// EGDE-624: the CMS still accepts up to 10 slides; anything past the sixth
// cannot be reached on mobile, so the extras are dropped rather than rendered.
const MAX_SLIDES = 6

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// EGDE-566: Umbraco reports "umbracoMediaVideo" (with an empty extension) for
// uploaded videos — strict equality never matched (cf. EGDE-36 in Footer).
function isVideoMedia(mediaPicker: unknown): boolean {
  if (!Array.isArray(mediaPicker) || mediaPicker.length === 0) return false
  const media = mediaPicker[0] as Record<string, unknown>
  const mediaType = String(media?.mediaType ?? "").toLowerCase()
  const extension = String(media?.extension ?? "").toLowerCase()
  return (
    mediaType.includes("video") ||
    ["mp4", "webm", "ogg", "mov"].includes(extension)
  )
}

function getResponsiveImageUrl(blockList: any) {
  const item = blockList?.items?.[0]
  const props = item?.content?.properties
  if (!props)
    return {
      desktop: null,
      mobile: null,
      hasMobileImage: false,
      alt: "",
      isDesktopVideo: false,
      isMobileVideo: false,
    }

  const desktopRel = getMediaUrl(props.desktopImage)
  const mobileRel = getMediaUrl(props.mobileImage)

  const isDesktopVideo = isVideoMedia(props.desktopImage)
  // EGDE-566 (r2): mobile showed an empty hero whenever the slide's media was a
  // video, because the video element was desktop-only and the mobile fallback
  // only existed if the editor had also uploaded a Mobile Image. Mobile now
  // falls back to the desktop asset — and plays it as a video when that is what
  // it is, instead of handing an .mp4 to next/image. An authored Mobile Image
  // still wins on mobile, and may itself be a video.
  const isMobileVideo = mobileRel
    ? isVideoMedia(props.mobileImage)
    : isDesktopVideo

  // ESEIM-163: Track whether a dedicated mobile image exists so the
  // component can decide to render a single universal image instead of
  // falling back desktop into the MobileImage wrapper.
  const hasMobileImage = !!mobileRel

  return {
    desktop: desktopRel ? getUmbracoMediaUrl(desktopRel) : null,
    mobile: mobileRel
      ? getUmbracoMediaUrl(mobileRel)
      : desktopRel
        ? getUmbracoMediaUrl(desktopRel)
        : null,
    hasMobileImage,
    alt: (props.altText as string) ?? "",
    isDesktopVideo,
    isMobileVideo,
  }
}

// EGDE-623: CTA Label and CTA Link are not mandatory in the CMS, so a hero can
// be saved with one of them missing. The button needs BOTH — a link with no
// label rendered as an empty orange pill — so it only shows when the pair is
// complete. EGDE-642 applies that same rule per slide.
function resolveCta(
  label: string | null | undefined,
  link: ApiLink[] | null | undefined,
): HeroCta | null {
  const trimmed = label?.trim()
  if (!trimmed) return null
  const resolved = resolveLinkHref(link, {
    getNonUmbracoUrl,
    getUmbracoMediaUrl,
  })
  if (!resolved?.href) return null
  return { label: trimmed, ...resolved }
}

// Link for internal content, plain anchor for media/external (ESEIM-173).
function CtaButton({ cta, state }: { cta: HeroCta; state: CtaState }) {
  const shared = {
    href: cta.href,
    target: cta.target,
    rel: cta.target === "_blank" ? "noopener noreferrer" : undefined,
    $state: state,
    "aria-hidden": state === "inactive" || undefined,
    tabIndex: state === "inactive" ? -1 : undefined,
  }
  return cta.isInternal ? (
    <CtaLink {...shared}>{cta.label}</CtaLink>
  ) : (
    <CtaAnchor {...shared}>{cta.label}</CtaAnchor>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function HeroBanner({ data }: Props) {
  const swiperRef = useRef<SwiperType | null>(null)
  const t = useTranslations("Homepage")
  // EGDE-618: the brand splash belongs to the homepage. It used to play on any
  // page that carried a hero banner, so adding one to an inner page (Why Us)
  // replayed the whole "אגד פלוס" reveal there. Same homepage test the header
  // and the notification strip already use — next/navigation keeps the locale
  // prefix, so `/en`, `/ar` and `/ru` roots count too.
  const pathname = usePathname()
  const isHomepage = pathname === "/" || /^\/[a-z]{2}\/?$/.test(pathname)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  // EGDE-331: intro reveal state
  const [introRevealed, setIntroRevealed] = useState(false) // hero content may appear
  const [introFinished, setIntroFinished] = useState(false) // overlay removed from DOM

  const autoRotateInterval = (data.autoRotateInterval ?? 5) * 1000

  // EGDE-624: at 375px the bottom strip only fits six progress bars before the
  // scroll button is pushed past the inline edge. Capping here is enough for the
  // whole block — the carousel slides, the stacked titles/subtitles, the nav
  // bars and the autoplay/loop decisions all read this list.
  const slides = (data.slides?.items ?? []).slice(0, MAX_SLIDES)
  const slideCount = slides.length

  // First slide background — shown through the aleph window during the intro.
  const firstSlideProps = slides[0]?.content.properties as
    | HeroBannerSlideProps
    | undefined
  const firstSlideImg = getResponsiveImageUrl(firstSlideProps?.backgroundImage)
  const hasIntroImage = !!(firstSlideImg.desktop || firstSlideImg.mobile)
  // EGDE-618: homepage-only, and only when there is a first image to reveal
  // through the aleph window.
  const wantsIntro = isHomepage && hasIntroImage
  const showIntro = !introFinished && wantsIntro

  // EGDE-726 r2: the page's ONE h1 is the ACTIVE slide's title (QA return
  // 2026-08-24: pinning it to slide 0 meant whoever inspected the VISIBLE
  // title mid-rotation found a <p>). The tag cannot swap the moment the slide
  // changes, though — swapping remounts the outgoing line, and a freshly
  // mounted inactive line paints at opacity 0, so its exit roll played
  // invisibly and the headline popped (the exact EGDE-497 r2 complaint).
  // The swap therefore waits for the 0.7s roll to finish: both lines keep
  // their tags for the whole visible move, then trade them at rest — the
  // outgoing one is hidden by then, and the incoming one suppresses the roll
  // replay its remount would otherwise trigger ($noRoll). A slide with no
  // title leaves the h1 on the first titled slide so it is never empty.
  const firstTitledIndex = Math.max(
    0,
    slides.findIndex(
      (s) => (s.content.properties as HeroBannerSlideProps | undefined)?.title,
    ),
  )
  const activeHasTitle = !!(
    slides[activeIndex]?.content.properties as HeroBannerSlideProps | undefined
  )?.title
  const headingTarget = activeHasTitle ? activeIndex : firstTitledIndex
  const [heading, setHeading] = useState({
    index: headingTarget,
    settled: false,
  })
  useEffect(() => {
    if (heading.index === headingTarget) return
    const timer = setTimeout(
      () => setHeading({ index: headingTarget, settled: true }),
      700, // the title roll's duration — see textRollIn/Out in styles
    )
    return () => clearTimeout(timer)
  }, [headingTarget, heading.index])

  const introRevealedRef = useRef(false)
  const handleIntroReveal = useCallback(() => {
    introRevealedRef.current = true
    setIntroRevealed(true)
  }, [])
  const handleIntroFinish = useCallback(() => setIntroFinished(true), [])

  // No intro to play (inner page per EGDE-618, or no usable first image) → hand
  // straight over to the hero. This must still run: the hero's title, CTA and
  // bottom strip are staged behind `introRevealed`, so leaving it false would
  // render the banner with no content on top of it.
  useEffect(() => {
    if (!wantsIntro) {
      introRevealedRef.current = true
      setIntroRevealed(true)
      setIntroFinished(true)
    }
  }, [wantsIntro])

  // EGDE-443: the white dip between slides was cut — QA read the flash as a
  // "weird white background". Slides now change with a plain crossfade and
  // the title/subtitle fade out-then-in (see TitleText/SubtitleText).

  // EGDE-331: hold the carousel on the first slide while the intro plays —
  // the dissolve must open into the same image the aleph window showed.
  useEffect(() => {
    if (introRevealed && slideCount > 1) {
      swiperRef.current?.autoplay?.start()
    }
  }, [introRevealed, slideCount])

  // Decorative image
  const decorativeImg = getResponsiveImageUrl(data.decorativeImage)

  // EGDE-642: the CTA belongs to the slide, not the banner — QA reported one
  // button shared across four slides. The banner-level pair no longer exists
  // in the CMS; each slide carries its own optional ctaLabel/ctaLink.
  const slideCtas = slides.map((slide) => {
    const slideProps = slide.content.properties as
      | HeroBannerSlideProps
      | undefined
    return resolveCta(slideProps?.ctaLabel, slideProps?.ctaLink)
  })

  // Every slide resolving to the same button (the migrated pre-EGDE-642
  // content) renders once and statically rather than re-animating an
  // identical label on every rotation.
  const uniformCta =
    slideCtas.length > 0 &&
    slideCtas.every(
      (cta) =>
        cta?.href === slideCtas[0]?.href && cta?.label === slideCtas[0]?.label,
    )
      ? slideCtas[0]
      : null
  const hasRotatingCtas = !uniformCta && slideCtas.some(Boolean)

  // Track cycle count to force progress bar remount when carousel loops
  const cycleRef = useRef(0)

  // Handle slide change
  const handleSlideChange = useCallback((swiper: SwiperType) => {
    const newIndex = swiper.realIndex
    setActiveIndex((prev) => {
      if (newIndex === 0 && prev > 0) cycleRef.current += 1
      return newIndex
    })
  }, [])

  // Toggle play/pause
  const toggleAutoplay = useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper) return

    setIsPlaying((prev) => {
      if (prev) swiper.autoplay.stop()
      else swiper.autoplay.start()
      return !prev
    })
  }, [])

  // Jump to a slide when its indicator is clicked. slideToLoop maps the logical
  // index to the correct (possibly duplicated) loop slide. (EGDE-287)
  const goToSlide = useCallback((index: number) => {
    swiperRef.current?.slideToLoop(index)
  }, [])

  // Scroll down handler. Inside a HeroScrollStage the sheet's rise consumes
  // the hero viewport PLUS the runway spacer — landing at the hero's bottom
  // would strand the visitor mid-transition, so target the end of the stage's
  // runway (sheet fully risen) instead.
  const handleScrollDown = useCallback(() => {
    const rootEl = document.querySelector("[data-hero-banner]")
    // Scope to THIS hero's own stage — a page-global lookup could grab a
    // different stage if a hero ever renders inside the sheet.
    const stage = rootEl?.closest("[data-hero-stage]")
    if (stage) {
      const runway = stage.querySelector<HTMLElement>("[data-hero-runway]")
      const top =
        stage.getBoundingClientRect().top +
        window.scrollY +
        (runway?.offsetHeight ?? 0) +
        window.innerHeight
      window.scrollTo({ top, behavior: "smooth" })
      return
    }
    if (rootEl) {
      const bottom = rootEl.getBoundingClientRect().bottom + window.scrollY
      window.scrollTo({ top: bottom, behavior: "smooth" })
    }
  }, [])

  return (
    <Root data-hero-banner>
      {/* Background carousel */}
      <SwiperWrapper>
        {/* EGDE-497: slides travel horizontally (new image slides in from the
            inline-start edge) per the Figma prototype — not a crossfade. */}
        <Swiper
          modules={[Autoplay]}
          autoplay={
            slideCount > 1
              ? { delay: autoRotateInterval, disableOnInteraction: false }
              : false
          }
          loop={slideCount > 1}
          speed={800}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            // EGDE-331: don't rotate slides behind the intro overlay.
            if (!introRevealedRef.current) swiper.autoplay?.stop()
          }}
          onSlideChange={handleSlideChange}
        >
          {slides.map((slide, index) => {
            const slideProps = slide.content
              .properties as HeroBannerSlideProps | undefined
            const bgImg = getResponsiveImageUrl(slideProps?.backgroundImage)
            return (
              <SwiperSlide key={slide.content.id ?? index}>
                <SlideImageWrapper>
                  {bgImg.isDesktopVideo || bgImg.isMobileVideo ? (
                    /* EGDE-566: a slide whose media is a video renders its own
                       per-viewport layer, so mobile is never left empty. Each
                       side plays a video or shows an image depending on what the
                       editor uploaded there (EGDE-100: an authored Mobile Image
                       still replaces the video on mobile). */
                    <>
                      {bgImg.desktop && (
                        <DesktopImage>
                          {bgImg.isDesktopVideo ? (
                            <SlideVideo
                              src={bgImg.desktop}
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="auto"
                            />
                          ) : (
                            <Image
                              src={bgImg.desktop}
                              alt={bgImg.alt}
                              fill
                              sizes="(min-width: 1280px) 100vw, 1px"
                              style={{ objectFit: "cover" }}
                              priority={index === 0}
                            />
                          )}
                        </DesktopImage>
                      )}
                      {bgImg.mobile && (
                        <MobileImage>
                          {bgImg.isMobileVideo ? (
                            <SlideVideo
                              src={bgImg.mobile}
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="auto"
                            />
                          ) : (
                            <Image
                              src={bgImg.mobile}
                              alt={bgImg.alt}
                              fill
                              sizes="(max-width: 1279px) 100vw, 1px"
                              style={{ objectFit: "cover" }}
                              priority={index === 0}
                            />
                          )}
                        </MobileImage>
                      )}
                    </>
                  ) : bgImg.hasMobileImage ? (
                    <>
                      {/* ESEIM-163: Separate desktop and mobile images */}
                      {bgImg.desktop && (
                        <DesktopImage>
                          <Image
                            src={bgImg.desktop}
                            alt={bgImg.alt}
                            fill
                            sizes="(min-width: 1280px) 100vw, 1px"
                            style={{ objectFit: "cover" }}
                            priority={index === 0}
                          />
                        </DesktopImage>
                      )}
                      {bgImg.mobile && (
                        <MobileImage>
                          <Image
                            src={bgImg.mobile}
                            alt={bgImg.alt}
                            fill
                            sizes="(max-width: 1279px) 100vw, 1px"
                            style={{ objectFit: "cover" }}
                            priority={index === 0}
                          />
                        </MobileImage>
                      )}
                    </>
                  ) : (
                    /* ESEIM-163: No dedicated mobile image — render a single
                       image that covers all viewports without breakpoint toggle */
                    bgImg.desktop && (
                      <Image
                        src={bgImg.desktop}
                        alt={bgImg.alt}
                        fill
                        sizes="100vw"
                        style={{ objectFit: "cover" }}
                        priority={index === 0}
                      />
                    )
                  )}
                </SlideImageWrapper>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </SwiperWrapper>

      {/* Dark overlay */}
      <Overlay />

      {/* Content on top of overlay */}
      <ContentLayer>
        <ContentFadeIn>
          <MainContent>
            {/*
             * RTL: Figma shows image on LEFT, text on RIGHT (visual).
             * In RTL flex-direction:row reverses source order.
             * So text comes FIRST, image SECOND in JSX.
             * On desktop, the flex-row reversal places image left, text right.
             * On mobile, column layout stacks: text first (top), image second.
             * The decorative image uses `order: -1` on mobile to appear on top.
             */}
            <TextContent $entered={introRevealed}>
              <TitleBlock>
                {/* Line 1: per-slide title (EGDE-442) — every slide's title is
                 * stacked in one grid cell and crossfades with the carousel.
                 * The banner-level Title field was removed from the CMS (QA
                 * 2026-07-08: "leftover, no longer in use"), so there is no
                 * block-level fallback anymore. */}
                {slideCount > 0 && (
                  <TitleTextWrapper>
                    {slides.map((slide, index) => {
                      const slideProps = slide.content
                        .properties as HeroBannerSlideProps | undefined
                      return (
                        <TitleText
                          key={slide.content.id ?? index}
                          variant="h1"
                          // EGDE-726: the hero owns the page's ONE h1 — a
                          // carousel of h1s handed the homepage eight of them.
                          // It follows the VISIBLE title, trading tags only
                          // once the roll has settled — see the `heading`
                          // state above for why the swap must wait.
                          component={index === heading.index ? "h1" : "p"}
                          $active={activeIndex === index}
                          $noRoll={
                            heading.settled &&
                            index === heading.index &&
                            index === activeIndex
                          }
                        >
                          {slideProps?.title}
                        </TitleText>
                      )
                    })}
                  </TitleTextWrapper>
                )}

                {/* Line 2: orange chevron + rotating subtitle.
                 * EGDE-24: Figma places the chevron at the inline-START of the
                 * subtitle (right of the text in RTL, left in LTR) pointing INTO
                 * the text. Rendering the chevron FIRST in source puts it at the
                 * inline-start automatically in both directions; orientation is
                 * handled in ChevronAccent. */}
                <SubtitleRow>
                  <ChevronAccent>
                    <GradientChevron />
                  </ChevronAccent>
                  <SubtitleTextWrapper>
                    {slides.map((slide, index) => {
                      const slideProps = slide.content
                        .properties as HeroBannerSlideProps | undefined
                      return (
                        <SubtitleText
                          key={slide.content.id ?? index}
                          variant="h1"
                          // EGDE-726: h1 is the style, not the role — the
                          // subtitle must never be a heading.
                          component="p"
                          $active={activeIndex === index}
                        >
                          {slideProps?.subtitle}
                        </SubtitleText>
                      )
                    })}
                  </SubtitleTextWrapper>
                </SubtitleRow>
              </TitleBlock>

              {/* CTA Button. EGDE-642: one button per slide, stacked in a
               * reserved slot so the band never collapses on a slide that
               * carries none — the button rolls in and out with the title
               * instead of the hero jumping. */}
              {uniformCta && <CtaButton cta={uniformCta} state="static" />}
              {hasRotatingCtas && (
                <CtaSlot>
                  {slides.map((slide, index) => {
                    const cta = slideCtas[index]
                    if (!cta) return null
                    return (
                      <CtaButton
                        key={slide.content.id ?? index}
                        cta={cta}
                        state={activeIndex === index ? "active" : "inactive"}
                      />
                    )
                  })}
                </CtaSlot>
              )}
            </TextContent>

            {/* Decorative Egged X image */}
            {decorativeImg.desktop && (
              <DecorativeImageWrapper $entered={introRevealed}>
                <Image
                  src={decorativeImg.desktop}
                  alt={decorativeImg.alt}
                  fill
                  sizes="(max-width: 1279px) 300px, 540px"
                  style={{ objectFit: "cover" }}
                />
              </DecorativeImageWrapper>
            )}
          </MainContent>
        </ContentFadeIn>

        {/* Bottom strip — a flex child of ContentLayer (NOT absolutely
         * positioned). EGDE-68: keeping it in the normal flow means the
         * content above (ContentFadeIn, flex:1) is laid out in the space that
         * remains AFTER the strip takes its natural height, so the CTA can
         * never overlap the carousel player regardless of how many slides
         * (progress bars) the player renders. No magic padding-bottom needed. */}
        <BottomStripOuter $entered={introRevealed}>
          <BottomStrip>
          {/*
           * RTL: Visual order is [scroll indicator RIGHT, nav+pause LEFT].
           * RTL flex-direction:row with justify-content:space-between.
           * Source order reversed: CarouselNavContainer first, ScrollIndicator second.
           */}
          {slideCount > 1 && (
            <CarouselNavContainer>
              <CircleIconButton
                onClick={toggleAutoplay}
                aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </CircleIconButton>

              <CarouselNavBars>
                {slides.map((slide, index) => {
                  let state: "idle" | "active" | "completed" = "idle"
                  if (index < activeIndex) {
                    state = "completed"
                  } else if (index === activeIndex) {
                    state = isPlaying ? "active" : "completed"
                  }
                  return (
                    <NavBarButton
                      key={slide.content.id ?? `nav-${index}`}
                      type="button"
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                      aria-current={index === activeIndex}
                    >
                      <NavBar>
                        <NavBarProgress
                          key={`${cycleRef.current}-${activeIndex}-${isPlaying}-${index}`}
                          $state={state}
                          $duration={autoRotateInterval}
                        />
                      </NavBar>
                    </NavBarButton>
                  )
                })}
              </CarouselNavBars>
            </CarouselNavContainer>
          )}

          <ScrollIndicator>
            {data.scrollIndicatorText && (
              <ScrollText variant="body1">
                {data.scrollIndicatorText}
              </ScrollText>
            )}
            <CircleIconButton
              onClick={handleScrollDown}
              aria-label="Scroll down"
            >
              <ArrowDown />
            </CircleIconButton>
          </ScrollIndicator>
          </BottomStrip>
        </BottomStripOuter>
      </ContentLayer>

      {/* EGDE-460: the splash overlay is part of the SSR HTML (the session
          check lives in a client effect), so on repeat visits it used to paint
          for the hydration gap and then vanish — QA read that flash as a
          broken, too-quick animation, and the page was scrollable until the
          effect ran. This script executes synchronously while the document is
          still parsing (React only executes it on the initial HTML load, never
          on client navigations): it stamps <html data-intro-played> so the
          GlobalStyle rules hide the overlay before first paint and skip the
          CSS scroll lock.
          EGDE-618: homepage-only, like the overlay it guards — an inner-page
          hero has no splash to hide. */}
      {wantsIntro && (
        <script
          dangerouslySetInnerHTML={{
            __html:
              'try{if(sessionStorage.getItem("heroIntroPlayed")==="true")document.documentElement.setAttribute("data-intro-played","")}catch(e){}',
          }}
        />
      )}

      {/* EGDE-331: brand intro reveal (plays once on first load) */}
      {showIntro && (
        <HeroIntro
          wordmark={t("brandWordmark")}
          tagline={t("brandTagline")}
          onReveal={handleIntroReveal}
          onFinish={handleIntroFinish}
        />
      )}
    </Root>
  )
}
